import {
  AppData,
  CompRef,
  DocumentServicesObject,
} from '@wix/document-services-types';
import {
  IPublicManifestAPI,
  PluginActionContext,
  PluginInstallationParams,
  UniversalSlotPointer,
  WidgetPointer,
} from '@wix/editor-platform-host-integration-apis';
import { ComponentRef } from '@wix/editor-platform-sdk-types';
import { PlatformContext } from '../../../types/platformApi';
import { createPluginHostApi } from './pluginHostAPI';
import { extendPluginActionContext } from './utils';
import { IExperimentsApiAdapter } from '../../utils/experimentsApiAdapter';
import { PluginActionFullContext } from './types';
import { SlotPointer } from './slotPointer';

// TODO: make autoPopulate logic part of initPluginsService()
async function navigateAsync(
  documentServicesAPI: DocumentServicesObject,
  pageId: string,
) {
  const promisifiedNavigation = new Promise<void>((resolve) => {
    documentServicesAPI.pages.navigateTo(
      pageId,
      () => {
        resolve();
      },
      () => {
        resolve(); // If we couldn't navigate we still want to try to add the plugin
      },
    );
  });
  return promisifiedNavigation;
}

function getSlotPointerInput(
  pluginInstallationParams: PluginInstallationParams,
): ComponentRef | UniversalSlotPointer {
  const slotPointer =
    pluginInstallationParams.slotComponentRef ??
    (
      pluginInstallationParams as PluginInstallationParams & {
        slotPointer?: ComponentRef | UniversalSlotPointer;
      }
    ).slotPointer;
  if (!slotPointer) {
    throw new Error(
      '[pluginAutoPopulateService] Missing slotComponentRef in plugin installation params.',
    );
  }
  return slotPointer;
}

async function addWidgetPlugin(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  manifestAPI: IPublicManifestAPI,
  slotPointerInput: ComponentRef | UniversalSlotPointer,
  widgetRef: WidgetPointer,
  actionContext: PluginActionFullContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef> {
  const hostAPI = createPluginHostApi(documentServicesAPI, experimentsAPI);
  const slotPointer = SlotPointer.create(
    documentServicesAPI,
    manifestAPI,
    experimentsAPI,
    slotPointerInput,
  );
  const slotInfo = hostAPI.getWidgetSlot(slotPointer, {
    includePrivate: false,
  });

  if (slotInfo?.pluginInfo !== undefined) {
    await hostAPI.uninstallWidgetPlugin(context, slotPointer, actionContext);
  }

  return hostAPI.installWidgetPlugin(
    context,
    slotPointer,
    widgetRef,
    actionContext,
  );
}

function populate(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  manifestAPI: IPublicManifestAPI,
  pluginInstallationParams: PluginInstallationParams,
  actionContext: PluginActionFullContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef> {
  return addWidgetPlugin(
    context,
    documentServicesAPI,
    manifestAPI,
    getSlotPointerInput(pluginInstallationParams),
    pluginInstallationParams.widgetRef,
    actionContext,
    experimentsAPI,
  );
}
export async function autoInstall(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  manifestAPI: IPublicManifestAPI,
  pluginInstallationParams: PluginInstallationParams,
  pluginActionContext: PluginActionContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef | null> {
  const actionContext = extendPluginActionContext(pluginActionContext);
  const pluginRef = await populate(
    context,
    documentServicesAPI,
    manifestAPI,
    pluginInstallationParams,
    actionContext,
    experimentsAPI,
  );

  return pluginRef;
}

export async function autoInstallWithNavigation(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  manifestAPI: IPublicManifestAPI,
  pluginInstallationParams: PluginInstallationParams,
  pluginActionContext: PluginActionContext,
  experimentsAPI: IExperimentsApiAdapter,
): Promise<CompRef | null> {
  await navigateAsync(documentServicesAPI, pluginInstallationParams.pageId);

  await documentServicesAPI.waitForChangesAppliedAsync();
  const actionContext = extendPluginActionContext(pluginActionContext);

  const pluginRef = await populate(
    context,
    documentServicesAPI,
    manifestAPI,
    pluginInstallationParams,
    actionContext,
    experimentsAPI,
  );

  await documentServicesAPI.waitForChangesAppliedAsync();

  await (documentServicesAPI.pages as any).scrollToComponent(pluginRef.id); // for some reason type is missing and its actually exists

  context.platformApiMethods.editor.selection.selectComponentByCompRef(
    {} as AppData,
    '',
    { compsToSelect: [pluginRef] },
  );

  return pluginRef;
}
